import React, { FC, ReactNode } from "react";
import reactStringReplace from "react-string-replace";
import { CSSProperties } from "styled-components";
import { CustomText } from "../../types/graphql/generated";
import { StyledExternalLink, StyledTemplate } from "./index.styled";

interface ComponentMap {
  [key: string]: ReactNode;
}

interface TemplateAsComponentProps {
  template: string;
  map: ComponentMap;
  className?: string;
  display?: CSSProperties["display"];
}

/**
 * TemplateAsComponent is used to interpolate React/HTML nodes integrated into a
 * cohesive sentence (text formatting like bold or italic; link components).
 * Ex. usage: terms and conditions
 * For simple string interpolation use parseTemplateAsString
 */
export const TemplateAsComponent: FC<TemplateAsComponentProps> = ({
  template,
  map,
  className,
  display,
}) => {
  const parsedTemplate = reactStringReplace(
    template,
    /(\$\{.+?})/g,
    (match) => {
      const key = match.substring(2, match.length - 1).trim();
      return map[key];
    }
  );

  return (
    <StyledTemplate $display={display} className={className}>
      {parsedTemplate.map((val, i) => (
        <React.Fragment key={i}>{val}</React.Fragment>
      ))}
    </StyledTemplate>
  );
};

export const getMapFromCustomText = ({
  customText,
  underlineLinks,
  disableLinking,
}: {
  customText: CustomText;
  underlineLinks?: boolean;
  disableLinking?: boolean;
}) => {
  return customText.links.reduce<Record<string, ReactNode>>((links, link) => {
    const { key, displayValue, linkValue } = link;

    let linkPrefix = "";
    if (link.type === "phone") {
      linkPrefix = "tel:";
    } else if (link.type === "email") {
      linkPrefix = "mailto:";
    }

    if (disableLinking) {
      links[key] = displayValue;
    } else {
      links[key] = (
        <StyledExternalLink
          href={`${linkPrefix}${linkValue}`}
          underline={underlineLinks}
        >
          {displayValue}
        </StyledExternalLink>
      );
    }

    return links;
  }, {});
};

interface TemplateAsComponentFromCustomText {
  customTexts?: CustomText[];
  disableLinking?: boolean;
  location: string;
  type: string;
  underlineLinks?: boolean;
}

/**
 * Transforms a singular custom text to one or more TemplateAsComponents
 * (if multiple sentences in custom text body)
 *
 * In order for this to work correctly, make sure that the variables in the
 * body EXACTLY MATCH the key in an individual link. ex:
 *
 * - body: Please contact ${supportLinkText}
 * - key: supportLinkText
 */
export const TemplateAsComponentFromCustomText: FC<
  TemplateAsComponentFromCustomText
> = ({
  customTexts,
  disableLinking = false,
  location,
  type,
  underlineLinks = false,
}) => {
  const customText = customTexts?.find((text) => {
    return text.location === location && text.type === type;
  });

  if (!customText || !customText?.body) return null;

  const map = getMapFromCustomText({
    customText,
    underlineLinks,
    disableLinking,
  });

  return (
    <>
      {customText.body.map((text) => (
        <TemplateAsComponent template={text} map={map} key={text} />
      ))}
    </>
  );
};
